// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import {
  SectionComponent,
  GSGrid,
  Button,
  ButtonGroup,
  RightPanel,
  TooltipDropdown,
  Text,
  View,
} from "@components";
import { compose } from "redux";

import { AbsenceReviewViewModal } from "@modules/shared/components";
import { formatDate } from "@transformers/date";
import actionCreator from "@modules/shared/actionCreator";
import reportingActionCreator from "@modules/reporting/actionCreator";
import * as selectors from "@modules/shared/selectors";
import * as employeeSelectors from "@modules/employee/selectors";
import { getSelectedTaskHistory } from "@modules/reporting/selectors/tasks";
import type { ApprovableContainer } from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import { getCurrentAbsenceMonitoring } from "@modules/service/selectors";

import { formatAuthoredBy } from "@transformers/names";
import {
  MonitoringUpdateModal,
  ManualReferralModal,
  AppealReview,
  SelectAbsenceMonitoringProcessModal,
} from "@modules/shared/components";
import type {
  EventHistoryItem,
  EventHistoryButtonTypes,
  ManualReferralTaskData,
} from "@modules/shared/reducer/employee-events/types";
import type {
  AbsenceReviewTask,
  ReferralDecisionTask,
} from "@modules/shared/reducer/absence-review/types";
import type { State } from "@store/reducers";
import { push } from "connected-react-router";
import { AbsenceRTWInterviewModal, ReferralDecisionModal } from "@modules/shared/components";
import type { RtwInterviewHistory } from "@modules/shared/reducer/employee-absences/types";
import serviceActionCreator from "@modules/service/actionCreator";

import { ManualReferralTaskModal } from "@modules/shared/components/index";

const APPEAL_STATUS_COLORS = {
  NOT_APPEALED: "text-color-pinkish-grey",
  ELIGIBLE: "text-color-black",
  SUBMITTED: "text-color-orange",
  UPHELD: "text-color-positive",
  NOT_UPHELD: "text-color-red",
  CANCELED: "text-color-blue",
};

const APPEAL_STATUS_TOOLTIPS = {
  NOT_APPEALED: "APPEAL_STATUS_NOT_APPEALED_TOOLTIP",
  ELIGIBLE: "APPEAL_STATUS_ELIGIBLE_TOOLTIP",
  SUBMITTED: "APPEAL_STATUS_SUBMITTED_TOOLTIP",
  UPHELD: "APPEAL_STATUS_UPHELD_TOOLTIP",
  NOT_UPHELD: "APPEAL_STATUS_NOT_UPHELD_TOOLTIP",
  CANCELED: "APPEAL_STATUS_CANCELED_TOOLTIP",
};

type OwnProps = $Exact<{
  employeeId: number,
  clientId: number,
  showSummary: boolean,
  enableMonitoringUpdate: boolean,
  enableManualReferral: boolean,
  gridSectionTitle: string,
  hideFilters: boolean,
  filters: Array<EventHistoryButtonTypes>,
  defaultFilter: EventHistoryButtonTypes,
  enableAppeals?: boolean,
  useAbsenceReviewColumns?: boolean,
}>;

type PropsFromState = $Exact<{
  eventHistoryList: Object,
  lastAbsenceReviewTask: ?AbsenceReviewTask,
  selectedAbsenceReviewTask: ?AbsenceReviewTask,
  selectReferralDecisionTaskData: ?ReferralDecisionTask,
  selectedRTWTask: ?RtwInterviewHistory,
  showManualReferralModal: boolean,
  isManualReferralAvailable: boolean,
  showHistoryModal: boolean,
  showAbsenceReviewModal: boolean,
  showReferralDecisionModal: boolean,
  showManualReferralTaskData: boolean,
  manualReferralTaskData: ManualReferralTaskData,
  currentEmployeeAccessLevel: string,
  currentEmployee: ?{ firstName: string },
  task: ?AbsenceReviewTask,
  absenceMonitoringConfig: ?ApprovableContainer<AbsenceMonitoring>,
}>;

type PropsFromDispatch = $Exact<{
  getEventHistory: typeof actionCreator.GetEmployeeEventHistory,
  getManualReferralEnabled: typeof actionCreator.GetManualReferralEnabled,
  getReferralProcesses: typeof actionCreator.GetReferralProcesses,
  getLastAbsenceReviewTask: typeof actionCreator.GetLastAbsenceReviewTask,
  getAbsenceReviewTask: typeof actionCreator.GetAbsenceReviewTask,
  getReferralDecisionTaskData: typeof actionCreator.GetReferralDecisionTaskData,
  getManualReferralTaskData: typeof actionCreator.GetManualReferralTaskData,
  getRtwInterviewTask: typeof reportingActionCreator.GetRtwInterviewTask,
  setRtwInterviewTaskHistory: typeof reportingActionCreator.SetRtwInterviewTaskHistory,
  toggleManualReferralModal: typeof actionCreator.ToggleManualReferralModal,
  toggleHistoryModal: typeof actionCreator.ToggleEventHistoryItemModal,
  toggleAbsenceReviewModal: typeof actionCreator.ToggleAbsenceReviewModal,
  toggleReferralDecisionModal: typeof actionCreator.ToggleReferralDecisionModal,
  toggleManualReferralTaskDataModal: typeof actionCreator.ToggleManualReferralTaskDataModal,
  resetHistory: typeof actionCreator.ResetEmployeeEventHistory,
  updateReviewTask: typeof actionCreator.GetLastAbsenceReviewTaskSuccess,
  getAbsenceMonitoring: typeof serviceActionCreator.GetAbsenceMonitoring,
  push: typeof push,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type CurrentAppeal = $Exact<{
  taskId: number,
}>;

type ComponentState = $Exact<{
  activeButton: EventHistoryButtonTypes,
  currentAppeal: CurrentAppeal | null,
  showSelectProcessModal: boolean,
  showUpdateModal: boolean,
  updateModalSkipCreate: boolean,
}>;

export class EventHistory extends React.Component<Props, ComponentState> {
  state = {
    activeButton: this.props.defaultFilter || "ALL",
    currentAppeal: null,
    showSelectProcessModal: false,
    showUpdateModal: false,
    updateModalSkipCreate: false,
  };

  componentDidMount() {
    this.props.getEventHistory({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      filters:
        this.state.activeButton !== "ALL" ? { taskType: this.state.activeButton } : undefined,
    });

    this.props.enableManualReferral &&
      this.props.getManualReferralEnabled({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    this.props.getReferralProcesses({
      clientId: this.props.clientId,
    });
  }
  componentDidUpdate(prevProps: Props) {
    if (
      this.props.eventHistoryList &&
      JSON.stringify(this.props.eventHistoryList) !== JSON.stringify(prevProps.eventHistoryList)
    ) {
      const lastAbsenceReviewTask =
        this.historyList &&
        this.historyList.find(item => item.taskType === "ABSENCE_MONITORING_REVIEW");

      if (lastAbsenceReviewTask) {
        this.props.getLastAbsenceReviewTask({
          clientId: this.props.clientId,
          taskId: lastAbsenceReviewTask.id,
        });
      }
    }

    if (!this.props.task && prevProps.task) {
      this.props.getEventHistory({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        filters:
          this.state.activeButton !== "ALL" ? { taskType: this.state.activeButton } : undefined,
      });
    }

    if (
      this.props.enableAppeals &&
      this.props.lastAbsenceReviewTask &&
      !prevProps.lastAbsenceReviewTask
    ) {
      this.props.getAbsenceMonitoring({
        clientId: this.props.clientId,
        processId: this.props.lastAbsenceReviewTask.absenceMonitoringProcessId,
      });
    }
  }
  getEventHistory = (buttonType: EventHistoryButtonTypes) => () => {
    this.setState({
      activeButton: buttonType,
    });

    const payload = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      filters: buttonType !== "ALL" ? { taskType: buttonType } : undefined,
    };
    this.props.getEventHistory(payload);
  };
  getButtonState = (buttonType: EventHistoryButtonTypes) => {
    return this.state.activeButton === buttonType ? "selected" : null;
  };
  toggleUpdateModal = () => {
    this.setState(prev => ({
      showUpdateModal: !prev.showUpdateModal,
      updateModalSkipCreate: false,
    }));
  };
  toggleHistoryModal = (item?: EventHistoryItem) => () => {
    if (item) {
      this.props.getRtwInterviewTask({
        clientId: this.props.clientId,
        taskId: item.id,
      });
    }

    this.props.setRtwInterviewTaskHistory(null);
    this.props.toggleHistoryModal(!this.props.showHistoryModal);
  };
  toggleAbsenceReviewTaskModal = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getAbsenceReviewTask({
        clientId: this.props.clientId,
        taskId: item.id,
      });
    }

    this.props.toggleAbsenceReviewModal(!this.props.showAbsenceReviewModal);
  };
  toggleEmployeeReferralDecision = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getReferralDecisionTaskData({
        clientId: this.props.clientId,
        taskId: item.id,
        referralType: "employee",
      });
      this.props.toggleReferralDecisionModal();
    }
  };
  toggleMangerReferralDecision = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getReferralDecisionTaskData({
        clientId: this.props.clientId,
        taskId: item.id,
        referralType: "manager",
      });
      this.props.toggleReferralDecisionModal();
    }
  };
  toggleManualReferralTask = (item?: EventHistoryItem) => {
    if (item) {
      this.props.getManualReferralTaskData({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        taskId: item.id,
      });
    }
  };

  handleTaskClick = (item: EventHistoryItem) => () => {
    switch (item.taskType) {
      case "ABSENCE_MONITORING_REVIEW":
        this.toggleAbsenceReviewTaskModal(item);
        break;

      case "EMPLOYEE_REFERRAL_DECISION":
        this.toggleEmployeeReferralDecision(item);
        break;

      case "MANAGER_REFERRAL_DECISION":
        this.toggleMangerReferralDecision(item);
        break;

      case "MANUAL_REFERRAL":
        this.toggleManualReferralTask(item);
        break;

      default:
        this.toggleHistoryModal(item)();
        break;
    }
  };

  openAppealModal = (taskId: number) => () => {
    this.setState({ currentAppeal: { taskId } });
  };

  closeAppealModal = () => {
    this.setState({ currentAppeal: null });
  };

  toggleSelectProcessModal = () => {
    this.setState(prev => ({ showSelectProcessModal: !prev.showSelectProcessModal }));
  };

  handleProcessSelected = () => {
    this.setState({
      showSelectProcessModal: false,
      showUpdateModal: true,
      updateModalSkipCreate: true,
    });
  };

  closeUpdateModal = () => {
    this.setState({ showUpdateModal: false, updateModalSkipCreate: false });
  };

  get filters() {
    return (
      this.props.filters || [
        "ALL",
        "ABSENCE_MONITORING_REVIEW",
        "RTW_INTERVIEW",
        "ALERT_TRIGGERED",
        "REFERRALS",
      ]
    );
  }

  get summaryColumns() {
    return [
      {
        key: "processName",
        name: this.props.t("PROCESS_NAME"),
      },
      {
        key: "levelOrder",
        name: this.props.t("LEVEL_NUMBER"),
        width: 140,
      },
      {
        key: "levelName",
        name: this.props.t("LEVEL_NAME"),
      },
      {
        key: "actionTakenBy",
        name: this.props.t("ACTION_TAKEN_BY"),
      },
      {
        key: "effectiveFrom",
        name: this.props.t("EFFECTIVE_FROM"),
      },
      {
        key: "reviewDate",
        name: this.props.t("REVIEW_DATE"),
      },
    ];
  }

  get isLastReviewForCurrentEmployee() {
    const lastReview = this.props.lastAbsenceReviewTask;
    return !!(
      lastReview &&
      lastReview.employee &&
      lastReview.employee.id === this.props.employeeId
    );
  }

  get summaryData() {
    const lastReview = this.isLastReviewForCurrentEmployee
      ? this.props.lastAbsenceReviewTask
      : null;
    return [
      {
        processName: lastReview
          ? this.props.absenceMonitoringConfig?.content?.name || this.props.t("N/A")
          : this.props.t("N/A"),
        levelOrder: lastReview?.currentLevel?.order,
        levelName:
          lastReview && lastReview.currentLevel
            ? lastReview.currentLevel.name
            : this.props.t("N/A"),
        actionTakenBy:
          lastReview && lastReview.completedBy ? lastReview.completedBy.name : this.props.t("N/A"),
        effectiveFrom:
          lastReview && lastReview.meetingDate
            ? formatDate(lastReview.meetingDate)
            : this.props.t("N/A"),
        reviewDate:
          lastReview && lastReview.nextReviewDate
            ? formatDate(lastReview.nextReviewDate)
            : this.props.t("N/A"),
      },
    ];
  }

  get isValid() {
    return (
      this.isLastReviewForCurrentEmployee &&
      this.props.lastAbsenceReviewTask &&
      this.props.lastAbsenceReviewTask.currentLevel
    );
  }

  get selectedRTWTask(): RtwInterviewHistory {
    return {
      completedBy: (this.props.selectedRTWTask || {}).completedBy,
      completionDate: (this.props.selectedRTWTask || { completionDate: "" }).completionDate,
      questionAnswers: (this.props.selectedRTWTask || { questionAnswers: [] }).questionAnswers,
      hasExpired: false,
      type: (this.props.selectedRTWTask || {}).type,
      id: (this.props.selectedRTWTask || {}).id,
      dueDate: this.props.selectedRTWTask.dueDate,
      rtwAt: this.props.selectedRTWTask.rtwAt,
    };
  }

  get historyList() {
    return this.props.eventHistoryList[this.state.activeButton].content;
  }
  getActivityTypeColor = (taskType: string) => {
    const typeColors = {
      ABSENCE_MONITORING_REVIEW: "background-color-ice",
      RTW_INTERVIEW: "background-color-coffee",
      EMPLOYEE_REFERRAL_DECISION: "background-color-berry",
      MANUAL_REFERRAL: "background-color-berry",
      MANAGER_REFERRAL_DECISION: "background-color-berry",
      ALERT_TRIGGERED: "background-color-yellow",
    };

    return typeColors[taskType] || "background-color-coffee";
  };

  get columns() {
    const rows = this.props.useAbsenceReviewColumns
      ? [
          {
            key: "id",
            name: this.props.t("REVIEW_ID"),
            width: 80,
          },
          {
            key: "createdBy",
            name: this.props.t("RECORDED_BY"),
            renderCell: ({ row }: { row: EventHistoryItem }) =>
              row.taskType === "ALERT_TRIGGERED"
                ? this.props.t("N/A")
                : formatAuthoredBy(row.createdBy),
          },
          {
            key: "date_recorded",
            name: this.props.t("DATE_RECORDED"),
            renderCell: ({ row }: { row: EventHistoryItem }) => formatDate(row.createdAt),
          },
          {
            key: "description",
            name: this.props.t("OUTCOME"),
            align: "left",
          },
          {
            key: "actions",
            name: this.props.t("VIEW_DETAILS"),
            width: 90,
            renderCell: ({ row }: { row: EventHistoryItem }) =>
              row.taskType === "ALERT_TRIGGERED" ||
              row.description === this.props.t("TASK_NOT_COMPLETED_YET") ? null : (
                <span className="material-icons pointer" onClick={this.handleTaskClick(row)}>
                  search
                </span>
              ),
          },
        ]
      : [
          {
            key: "id",
            name: this.props.t("ID"),
          },
          {
            key: "createdAt",
            name: this.props.t("ACTIVITY_DATE"),
            renderCell: ({ row }: { row: EventHistoryItem }) => formatDate(row.createdAt),
          },
          {
            key: "taskType",
            name: this.props.t("ACTIVITY_TYPE"),
            renderCell: ({ row }: { row: EventHistoryItem }) =>
              GSGrid.render.statusPill(
                this.props.t(row.taskType),
                null,
                `w-170-px text-color-black ${this.getActivityTypeColor(row.taskType)}`,
              ),
          },
          {
            key: "description",
            name: this.props.t("DESCRIPTION"),
            align: "left",
          },
          {
            key: "createdBy",
            name: this.props.t("RECORDED_BY"),
            renderCell: ({ row }: { row: EventHistoryItem }) =>
              row.taskType === "ALERT_TRIGGERED"
                ? this.props.t("N/A")
                : formatAuthoredBy(row.createdBy),
          },
          {
            key: "date_recorded",
            name: this.props.t("DATE_RECORDED"),
            renderCell: ({ row }: { row: EventHistoryItem }) => formatDate(row.createdAt),
          },
          {
            key: "actions",
            name: this.props.t("VIEW_DETAILS"),
            width: 90,
            renderCell: ({ row }: { row: EventHistoryItem }) =>
              row.taskType === "ALERT_TRIGGERED" ||
              row.description === this.props.t("TASK_NOT_COMPLETED_YET") ? null : (
                <span className="material-icons pointer" onClick={this.handleTaskClick(row)}>
                  search
                </span>
              ),
          },
        ];

    if (
      this.props.enableAppeals &&
      this.state.activeButton === "ABSENCE_MONITORING_REVIEW" &&
      this.props.absenceMonitoringConfig?.appealConfig?.isActive
    ) {
      rows.push({
        key: "absenceMonitoringReviewTaskAppealStatus",
        name: this.props.t("APPEAL"),
        width: 80,
        renderCell: ({ row }: { row: EventHistoryItem }) =>
          row.taskType === "ABSENCE_MONITORING_REVIEW" &&
          row.absenceMonitoringReviewTaskAppealStatus && (
            <TooltipDropdown
              className="pt-2"
              id={`appeal-status-${row.id}`}
              boundariesElement="viewport"
              description={
                <span
                  dangerouslySetInnerHTML={{
                    __html: this.props.t(
                      APPEAL_STATUS_TOOLTIPS[
                        row.absenceMonitoringReviewTaskAppealStatus || "NOT_UPHELD"
                      ],
                    ),
                  }}
                />
              }
            >
              <span
                className={`material-icons pointer ${
                  APPEAL_STATUS_COLORS[row.absenceMonitoringReviewTaskAppealStatus]
                }`}
                onClick={
                  row.absenceMonitoringReviewTaskAppealStatus !== "NOT_APPEALED" &&
                  this.openAppealModal(row.id)
                }
              >
                flag
              </span>
            </TooltipDropdown>
          ),
      });
    }

    return rows;
  }

  get data() {
    let list = this.historyList.filter((record: EventHistoryItem) => {
      return !(record.createdBy === null && record.taskType === "ABSENCE_MONITORING_REVIEW");
    });

    if (this.props.useAbsenceReviewColumns) {
      list = list.filter(
        (record: EventHistoryItem) =>
          !(record.description && record.description.includes("Maintained Current Status")),
      );
    }

    return list;
  }

  render() {
    return (
      <View gap="6">
        {this.props.showSummary && (
          <SectionComponent wrapperClassName="flex-column" className="gs-container">
            <View direction="row" justifyContent="space-between" margin={[0, 0, 3, 0]}>
              <Text type="h5">{this.props.t("CURRENT_LEVEL")}</Text>
              {this.props.enableMonitoringUpdate && this.isLastReviewForCurrentEmployee && (
                <View direction="row" gap={2}>
                  {this.props.currentEmployeeAccessLevel == "FULL_DETAILS" && (
                    <Button
                      materialIcon="rebase_edit"
                      disabled={!this.isValid}
                      onClick={this.toggleUpdateModal}
                    >
                      {this.props.t("CHANGE_LEVEL")}
                    </Button>
                  )}
                  {this.props.enableManualReferral && (
                    <Button
                      disabled={!this.props.isManualReferralAvailable}
                      onClick={this.props.toggleManualReferralModal}
                      materialIcon="add_box"
                    >
                      {this.props.t("ADD")}
                    </Button>
                  )}
                </View>
              )}
            </View>
            {this.isLastReviewForCurrentEmployee ? (
              <GSGrid rows={this.summaryData} columns={this.summaryColumns} />
            ) : (
              <View direction="row" justifyContent="space-between" alignItems="center">
                <Text size={1.5} color="grey">
                  {this.props.t("EMPLOYEE_NOT_IN_AM_PROCESS")}
                </Text>
                <Button onClick={this.toggleSelectProcessModal}>
                  {this.props.t("ENTER_PROCESS", {
                    firstName: this.props.currentEmployee?.firstName || "",
                  })}
                </Button>
              </View>
            )}
          </SectionComponent>
        )}

        <SectionComponent
          wrapperClassName="flex-column"
          title={<h5>{this.props.gridSectionTitle || ""}</h5>}
          className="gs-container"
        >
          <div className="d-flex justify-content-between mb-4 activity-history">
            <ButtonGroup className="d-flex w-100">
              {this.filters.length > 1 &&
                !this.props.hideFilters &&
                this.filters.map(filter => {
                  return (
                    <Button
                      key={filter}
                      size="small"
                      color={this.getButtonState(filter)}
                      onClick={this.getEventHistory(filter)}
                    >
                      {this.props.t(filter)}
                    </Button>
                  );
                })}
            </ButtonGroup>

            {this.props.enableMonitoringUpdate && this.state.showUpdateModal && (
              <MonitoringUpdateModal
                clientId={this.props.clientId}
                employeeId={this.props.employeeId}
                onCancel={this.closeUpdateModal}
                skipCreate={this.state.updateModalSkipCreate}
              />
            )}

            {this.props.enableManualReferral && this.props.showManualReferralModal && (
              <ManualReferralModal
                clientId={this.props.clientId}
                employeeId={this.props.employeeId}
                t={this.props.t}
              />
            )}

            {this.props.showManualReferralTaskData && (
              <ManualReferralTaskModal
                toggleModal={this.props.toggleManualReferralTaskDataModal}
                data={this.props.manualReferralTaskData}
                t={this.props.t}
              />
            )}

            {this.props.showHistoryModal && this.props.selectedRTWTask ? (
              <AbsenceRTWInterviewModal
                task={this.selectedRTWTask}
                onCancel={this.toggleHistoryModal()}
              />
            ) : null}
            {this.props.showAbsenceReviewModal && this.props.selectedAbsenceReviewTask ? (
              <AbsenceReviewViewModal
                task={this.props.selectedAbsenceReviewTask}
                onCancel={this.toggleAbsenceReviewTaskModal}
              />
            ) : null}
          </div>
          {this.props.showReferralDecisionModal && this.props.selectReferralDecisionTaskData && (
            <ReferralDecisionModal
              toggleModal={this.props.toggleReferralDecisionModal}
              data={this.props.selectReferralDecisionTaskData}
            />
          )}
          {this.state.currentAppeal && (
            <RightPanel
              title={this.props.t("APPEAL")}
              onClose={this.closeAppealModal}
              titleClassName="pb-1"
            >
              <AppealReview
                taskId={this.state.currentAppeal.taskId}
                onClose={this.closeAppealModal}
                clientId={this.props.clientId}
                employeeId={this.props.employeeId}
              />
            </RightPanel>
          )}
          {this.state.showSelectProcessModal && (
            <SelectAbsenceMonitoringProcessModal
              clientId={this.props.clientId}
              employeeId={this.props.employeeId}
              onCancel={this.toggleSelectProcessModal}
              onSelect={this.handleProcessSelected}
            />
          )}
          <GSGrid rows={this.data} columns={this.columns} />
        </SectionComponent>
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  eventHistoryList: selectors.getEventHistoryList(state),
  lastAbsenceReviewTask: selectors.getLastAbsenceReviewTask(state),
  selectedAbsenceReviewTask: selectors.getSelectedAbsenceReviewTask(state),
  selectReferralDecisionTaskData: selectors.selectReferralDecisionTask(state),
  selectedRTWTask: getSelectedTaskHistory(state),
  showManualReferralModal: selectors.showManualReferralModal(state),
  isManualReferralAvailable: selectors.isManualReferralAvailable(state),
  showHistoryModal: selectors.showHistoryItemModal(state),
  showAbsenceReviewModal: selectors.showAbsenceReviewModal(state),
  showReferralDecisionModal: selectors.showReferralDecisionModal(state),
  showManualReferralTaskData: selectors.showManualReferralTaskData(state),
  manualReferralTaskData: selectors.selectManualReferralTaskData(state),
  currentEmployeeAccessLevel: employeeSelectors.getCurrentEmployeeAccessLevel(state),
  currentEmployee: employeeSelectors.getCurrentEmployee(state),
  task: selectors.getReviewTask(state),
  absenceMonitoringConfig: getCurrentAbsenceMonitoring(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEventHistory: actionCreator.GetEmployeeEventHistory,
  resetHistory: actionCreator.ResetEmployeeEventHistory,
  updateReviewTask: actionCreator.GetLastAbsenceReviewTaskSuccess,
  getManualReferralEnabled: actionCreator.GetManualReferralEnabled,
  getReferralProcesses: actionCreator.GetReferralProcesses,
  getLastAbsenceReviewTask: actionCreator.GetLastAbsenceReviewTask,
  getAbsenceReviewTask: actionCreator.GetAbsenceReviewTask,
  getReferralDecisionTaskData: actionCreator.GetReferralDecisionTaskData,
  getManualReferralTaskData: actionCreator.GetManualReferralTaskData,
  getRtwInterviewTask: reportingActionCreator.GetRtwInterviewTask,
  setRtwInterviewTaskHistory: reportingActionCreator.SetRtwInterviewTaskHistory,
  toggleManualReferralModal: actionCreator.ToggleManualReferralModal,
  toggleHistoryModal: actionCreator.ToggleEventHistoryItemModal,
  toggleAbsenceReviewModal: actionCreator.ToggleAbsenceReviewModal,
  toggleReferralDecisionModal: actionCreator.ToggleReferralDecisionModal,
  toggleManualReferralTaskDataModal: actionCreator.ToggleManualReferralTaskDataModal,
  getAbsenceMonitoring: serviceActionCreator.GetAbsenceMonitoring,
  push,
};

export default compose(connect(mapStateToProps, mapDispatchToProps), localize())(EventHistory);
